import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventPlatformApi, type PublisherRecord, type ConsumerRecord, type SagaRecord } from '../../api/eventplatform';

const COMMERCE_MODULES = [
  'catalog', 'productstructure', 'productattributes', 'pricing', 'inventory',
  'listings', 'marketplacesync', 'marketplaceaccount', 'orders', 'warehouseexec',
  'logistics', 'returns', 'financecommerce', 'procurement', 'supplier', 'crm',
];

export function CommerceEventCatalogPage() {
  const [publishers, setPublishers] = useState<PublisherRecord[]>([]);
  const [consumers, setConsumers] = useState<ConsumerRecord[]>([]);
  const [sagas, setSagas] = useState<SagaRecord[]>([]);
  const [error, setError] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');

  useEffect(() => {
    void Promise.all([
      eventPlatformApi.publishers(),
      eventPlatformApi.consumers(),
      eventPlatformApi.sagas(),
    ])
      .then(([p, c, s]) => {
        setPublishers(p.publishers);
        setConsumers(c.consumers);
        setSagas(s.sagas);
      })
      .catch((e) => setError(String(e)));
  }, []);

  const filteredPublishers = moduleFilter === 'all'
    ? publishers
    : publishers.filter((p) => p.module_id === moduleFilter);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/events" className="text-sm text-blue-600 hover:underline dark:text-blue-400">← Event Hub</Link>
      <h1 className="mt-2 text-2xl font-semibold">Commerce Event Integration</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        DEV-000054 — enterprise domain and integration events for commerce modules.
      </p>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setModuleFilter('all')}
          className={`rounded-lg px-3 py-1 text-sm ${moduleFilter === 'all' ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'border dark:border-slate-700'}`}
        >
          All modules
        </button>
        {COMMERCE_MODULES.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setModuleFilter(m)}
            className={`rounded-lg px-3 py-1 text-sm ${moduleFilter === m ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'border dark:border-slate-700'}`}
          >
            {m}
          </button>
        ))}
      </div>
      <section className="mt-8">
        <h2 className="text-lg font-medium">Publishers ({filteredPublishers.length})</h2>
        <div className="mt-3 space-y-2">
          {filteredPublishers.map((p) => (
            <div key={p.id} className="rounded-lg border p-3 font-mono text-sm dark:border-slate-700">
              {p.event_name} <span className="text-slate-500">· {p.module_id}</span>
            </div>
          ))}
          {filteredPublishers.length === 0 && (
            <p className="text-sm text-slate-500">No publishers registered yet. Commerce services register on startup.</p>
          )}
        </div>
      </section>
      <section className="mt-8">
        <h2 className="text-lg font-medium">Consumers ({consumers.length})</h2>
        <div className="mt-3 space-y-2">
          {consumers.filter((c) => moduleFilter === 'all' || c.module_id === moduleFilter).map((c) => (
            <div key={c.id} className="rounded-lg border p-3 font-mono text-sm dark:border-slate-700">
              {c.event_name} <span className="text-slate-500">· {c.module_id} · {c.health}</span>
            </div>
          ))}
        </div>
      </section>
      <section className="mt-8">
        <h2 className="text-lg font-medium">Saga Monitor ({sagas.length})</h2>
        <div className="mt-3 space-y-2">
          {sagas.map((s) => (
            <div key={s.id} className="rounded-lg border p-3 dark:border-slate-700">
              <p className="font-medium">{s.name}</p>
              <p className="text-sm text-slate-500">{s.status} · {s.correlation_id ?? 'no correlation'}</p>
            </div>
          ))}
          {sagas.length === 0 && <p className="text-sm text-slate-500">No active sagas.</p>}
        </div>
      </section>
    </div>
  );
}
